import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  RotateCcw,
  Trash2,
  StickyNote,
  CheckSquare,
  Folder as FolderIcon,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useListTrash,
  useRestoreTrashItem,
  usePermanentlyDeleteTrashItem,
  type TrashItem,
} from "@workspace/api-client-react";
import { cn } from "@/lib/utils";

const TYPE_ICON: Record<TrashItem["type"], typeof StickyNote> = {
  note: StickyNote,
  task: CheckSquare,
  folder: FolderIcon,
};

const TYPE_LABEL: Record<TrashItem["type"], string> = {
  note: "Note",
  task: "Task",
  folder: "Folder",
};

const RETENTION_DAYS = 30;

function daysRemaining(deletedAt: string): number {
  const deletedTime = new Date(deletedAt).getTime();
  const elapsedDays = (Date.now() - deletedTime) / (1000 * 60 * 60 * 24);
  return Math.max(0, Math.ceil(RETENTION_DAYS - elapsedDays));
}

export function TrashPage() {
  const queryClient = useQueryClient();
  const { data: items = [], isLoading } = useListTrash();
  const restoreItem = useRestoreTrashItem();
  const permanentlyDelete = usePermanentlyDeleteTrashItem();

  const sorted = [...items].sort(
    (a, b) => new Date(b.deletedAt).getTime() - new Date(a.deletedAt).getTime(),
  );

  const invalidateAfterChange = () => {
    queryClient.invalidateQueries();
  };

  const handleRestore = (item: TrashItem) => {
    restoreItem.mutate(
      { type: item.type, id: item.id },
      { onSuccess: invalidateAfterChange },
    );
  };

  const handleDeleteForever = (item: TrashItem) => {
    const confirmed = window.confirm(
      "Permanently delete this " + TYPE_LABEL[item.type].toLowerCase() + "? This cannot be undone.",
    );
    if (!confirmed) return;

    permanentlyDelete.mutate(
      { type: item.type, id: item.id },
      { onSuccess: invalidateAfterChange },
    );
  };

  return (
    <div className="max-w-4xl mx-auto h-[calc(100vh-4rem)] flex flex-col px-4">
      <div className="pt-4 pb-2 shrink-0">
        <p className="text-sm text-muted-foreground">
          Deleted items stay here for {RETENTION_DAYS} days before they are
          permanently removed.
        </p>
      </div>

      <ScrollArea className="flex-1 -mx-4 px-4 pb-6">
        <div className="flex flex-col gap-3 py-2">
          {isLoading ? (
            <div className="text-center text-muted-foreground py-10">
              Loading trash...
            </div>
          ) : sorted.length === 0 ? (
            <div className="text-center text-muted-foreground py-20">
              Trash is empty.
            </div>
          ) : (
            sorted.map((item) => {
              const Icon = TYPE_ICON[item.type];
              const remaining = daysRemaining(item.deletedAt);
              return (
                <div
                  key={item.type + "-" + item.id}
                  className="bg-card border rounded-2xl px-4 py-3.5 shadow-sm flex items-center gap-3"
                >
                  <div className="h-9 w-9 shrink-0 rounded-full bg-muted flex items-center justify-center">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-medium truncate">
                      {item.title}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-muted-foreground">
                        {TYPE_LABEL[item.type]}
                      </span>
                      <span className="text-xs text-muted-foreground">.</span>
                      <span
                        className={cn(
                          "text-xs font-medium",
                          remaining <= 3 ? "text-red-500" : "text-muted-foreground",
                        )}
                      >
                        {remaining === 0
                          ? "Deleting soon"
                          : remaining + (remaining === 1 ? " day left" : " days left")}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRestore(item)}
                    className="p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-primary"
                    aria-label="Restore"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteForever(item)}
                    className="p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-red-500"
                    aria-label="Delete forever"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
